import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      await loginWithToken(data.token, data.user);
      toast.success("Account created! Let's set up your workspace.");
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Start your free trial" subtitle="No credit card required">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Your name</Label>
          <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name-input" className="mt-1" placeholder="Jane Smith" />
        </div>
        <div>
          <Label htmlFor="business">Business name</Label>
          <Input id="business" value={form.business_name} onChange={set("business_name")} data-testid="register-business-input" className="mt-1" placeholder="Smith Plumbing Co." />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input" className="mt-1" placeholder="you@company.com" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={8} data-testid="register-password-input" className="mt-1" placeholder="At least 8 characters" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full h-11 bg-blue-600 hover:bg-blue-700">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account? <Link to="/login" className="text-blue-600 font-medium hover:underline" data-testid="register-login-link">Log in</Link>
      </p>
    </AuthShell>
  );
}
